import { useCallback, useState } from 'react';
import { releaseNotes } from '@/config/releaseNotes';
import { isWhatsNewUnread } from './isWhatsNewUnread';

const STORAGE_KEY = 'qfly_seen_version';

const currentVersion: string | undefined = releaseNotes[0]?.version;

/**
 * `localStorage` read/write wrapped exactly like `useSidebarCollapsed.ts`'s
 * `readPersisted`/`writePersisted` — private browsing, blocked storage, or
 * a strict CSP can throw a `SecurityError` here. Swarm review (Security
 * Auditor, WNB-3) classified an unguarded version of this as blocking:
 * failure must fall back to "unread" (fail OPEN on the dot), never silently
 * resolve to "seen" — a storage error must not hide a real release from a
 * user who hasn't seen it.
 */
const readLastSeenVersion = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeLastSeenVersion = (version: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, version);
  } catch {
    // localStorage unavailable — best-effort only; the dot simply won't
    // clear, which is the fail-open direction we want (see above).
  }
};

export interface WhatsNewUnreadState {
  readonly unread: boolean;
  /** Call when the popover opens — clears the dot for this version. */
  readonly markSeen: () => void;
}

/**
 * No cross-instance broadcast bridge here (unlike `useSidebarCollapsed`,
 * which genuinely needs one — it has two separate mount points, the Drawer
 * and the mobile TopBar, that must stay in lock-step). This feature mounts
 * once; the row owns this hook directly and renders its own popover as a
 * child (Swarm arbitration, WNB-3: State Management's parent-child
 * recommendation, adopted over a broadcast-bridge alternative that solves
 * a problem this component doesn't have).
 *
 * `unread` is computed via a lazy `useState` initializer — synchronous at
 * first render, not a `useEffect` — so there is no flash-of-missing-dot on
 * mount (Performance Critic, WNB-3) and no stale-closure risk from an
 * effect dependency that never actually changes (State Management, WNB-3).
 */
export const useWhatsNewUnread = (): WhatsNewUnreadState => {
  const [unread, setUnread] = useState<boolean>(() =>
    isWhatsNewUnread(currentVersion, readLastSeenVersion()),
  );

  const markSeen = useCallback((): void => {
    if (currentVersion === undefined) return;
    writeLastSeenVersion(currentVersion);
    setUnread(false);
  }, []);

  return { unread, markSeen };
};
